<script setup>
import { useRoute } from 'vue2-helpers/vue-router';
import { useStore } from 'vue2-helpers/vuex';
import {ref, watch, computed, defineProps, provide, onMounted, nextTick} from 'vue';

import useSearch from "@/composables/search/useSearch";
import TopBar from "@/views/search/SearchTopBar.vue";
import Radar from "@/views/search/radar/Radar.vue";
import SaveSearch from "@/components/search/SaveSearch.vue";

import {constants, columnsComponentsNames} from "@/views/search/searchConstants";
import globalConstants from "@/constants/constants";
import usePromiseAll from "@/composables/usePromiseAll";
import searchService from "@/services/search.service";
import SideBar from "@/views/search/SearchSideBar.vue";
import useSavedSearches from "@/composables/search/useSavedSearches";
import {accountTypeEventLimited} from "@/constants/accountTypes";
import SearchGridActions from "@/views/search/SearchGridActions.vue";
import Helper from "@/components/Helper.vue";
import startupsService from "@/services/startups.service";
import ecosystemsService from "@/services/ecosystems.service";
import StartupsGrid from "@/views/search/StartupsGrid.vue";
import StartupsAdvancedGrid from "@/views/search/StartupsAdvancedGrid.vue";
import userColumnsService from "@/services/user-columns.service";

const {customView = null, searchType = constants.SEARCH_TYPE_STARTUPS} = defineProps(['customView','searchType']);
const emit = defineEmits(['invite']);

const store = useStore();
const route = useRoute();

// GET query params
const initSavedSearch = route.query.query;
const userSearch = route.query.search;
const searchCategory = route.query.category;
const searchTag = route.query.tag;
const searchClient = route.query.client;

// Use Search
const {query, promptQuery, searchParams, filters, customDataFilter, notWatchSearchParams, filtersOptions, suggestedAiParams,
    aiLoading, aiFailed, getAIQueryParams, applyAiQueryParams, queryText, negativeQueryText,
    filtersOptionsMethods: {getInvestorsList, getTopInvestorsList, getLocationsList, getTopLocationsList, getTagsList,
        getTopClientsList, getClientsList, getTopEcosystemsList, getEcosystemsList, getTrackersList},
    addKeyword, deleteRule, deleteAllRules, invertRule, toggleCondition, populateFilters, updateCustomDataFilter} = useSearch(getList);

// Use Saved Searches
const {savedSearch, savedSearches, refinedSavedSearch, getSavedSearches, getUserSearch} = useSavedSearches(searchType, initSavedSearch)

// Data
// -------------------------------------------------------------------------------------------------
const displayType = ref(constants.DISPLAY_TYPE_LIST);
const leftCollapsed = ref(false);
const asideTabIndex = ref(0);
const elasticQuery = ref(null);
const topBar = ref(null);
const gridActions = ref(null);
const saveSearchModal = ref(null);
const needUpgradeModal = ref(null);
const startupsGridMain = ref(null);
const startupsGridAI = ref(null);
const canUpdateSavedSearch = ref(false);
const canLoadList = ref(false);
const searchString = ref("");
const searchParamsFromSavedSearch = ref(false);
const gridType = ref('main');
const hybrid = ref(false);
const gridColumns = ref([]);

const gridSort = ref({
    field: '',
    dir: '',
});

const loadListResolvers = {
    settings: null,
    columns: null,
    initSavedSearch: null,
    userSearch: null,
    categories: null,
    tags: null,
    clients: null
};

// Computed
// -------------------------------------------------------------------------------------------------
const isStartupsSearch = computed(() => searchType === constants.SEARCH_TYPE_STARTUPS);
const isListView = computed(() => displayType.value === constants.DISPLAY_TYPE_LIST);
const isRadarView = computed(() => displayType.value === constants.DISPLAY_TYPE_RADAR);
const showRadarSwitcher = computed(() => isStartupsSearch.value && isListView.value);

const searchData = computed(() => ({
        rules: query.rules,
        prompt_query: promptQuery,
        search_params: searchParams,
    }));

const startupsGrid = computed(() => gridType.value === 'ai' ? startupsGridAI.value : startupsGridMain.value);
const itemsIds = computed(() => startupsGrid.value?.items.map(item => item.id));

// Watchers
// -------------------------------------------------------------------------------------------------
watch(() => store.state.user.id, () => {
    applySettings();
});

watch(canLoadList, value => {
    //If Ai prompt exists in url query then postpone list load until AI processed
    if (value && !(searchType === constants.SEARCH_TYPE_STARTUPS && route.query.ai_prompt && !store.getters.settings.disable_ai)) {
        getList();
    }
});

// Watch applied saved search
watch(refinedSavedSearch, (value) => {
    populateFilters(value.rules);
    query.rules = value.rules;

    if (value.prompt_query) {
        promptQuery.rules = value.prompt_query.rules;
        promptQuery.params = value.prompt_query.params;
    } else {
        promptQuery.rules = [];
        promptQuery.params.text = "";
        promptQuery.params.rewritten_query = "";
        promptQuery.params.exclusions = [];
    }

    if (value.search_params) {
        for (let key in value.search_params) {
            searchParams[key] = value.search_params[key];
        }

        searchParamsFromSavedSearch.value = true;
    }

    loadListResolvers.initSavedSearch();
});

watch(() => filtersOptions.categories, () => {
    checkCategories();
});

watch(() => filtersOptions.tags, () => {
    checkTags();
});

watch(() => filtersOptions.clients, () => {
    checkClients();
});

watch([query, promptQuery], () => {
    if (savedSearch.value) {
        canUpdateSavedSearch.value = true;
    }
});

watch(savedSearch, () => {
    canUpdateSavedSearch.value = false;
});

watch(() => store.getters.trackersList, () => {
    if (store.getters.isEnterprise) {
        getTrackersList();
    }
}, {immediate: true});

watch(() => store.state.needUpdateStartups || store.state.needUpdateUsers, () => {
    if (store.state.needUpdateStartups || store.state.needUpdateUsers) {
        getList();
    }
});

watch([gridType, hybrid], () => {
    startupsGrid.value.resetList();
});

// Lifecycle hooks
// -------------------------------------------------------------------------------------------------
onMounted(() => {
    // Trigger getList() function when all the prerequisites are fulfilled
    usePromiseAll(loadListResolvers, () => {canLoadList.value = true});

    if (!initSavedSearch) {
        loadListResolvers.initSavedSearch();
    }

    getColumns();
    getSavedSearches();
    getTopLocationsList();
    applySettings();

    if (userSearch) {
        precessUserSearch();
    } else {
        loadListResolvers.userSearch();
    }
});


// Methods
// -------------------------------------------------------------------------------------------------

function applySettings() {
    let settings = store.getters.settings;

    if (store.state.user.id) {
        if (store.getters.isEnterprise) {
            notWatchSearchParams.value = true;

            if (!searchParamsFromSavedSearch.value) {
                if (store.state.user.company_id === globalConstants.companyPfizer && settings.search_big_companies) {
                    searchParams.includeHidden = true;
                }

                searchParams.includeInactive = !!settings.search_inactive_companies;
                searchParams.includeAcquired = !!settings.search_acquired_companies;
            }

            if (store.state.user.account_type === accountTypeEventLimited) {
                gridSort.value = {
                    field: 'irl_score',
                    dir: 'desc',
                };
            }

            nextTick(() => notWatchSearchParams.value = false);

            if (searchCategory) {
                checkCategories();
            } else {
                loadListResolvers.categories();
            }

            if (searchTag) {
                checkTags();
            } else {
                loadListResolvers.tags();
            }

            if (searchClient) {
                checkClients();
            } else {
                loadListResolvers.clients();
            }

            getTagsList();
            getTopEcosystemsList();
            store.dispatch('fetchTrackers');

            // Add restriction rule
            if (store.getters.isFreeEnterprise) {
                store.state.user.connected_ecosystems.forEach(name => {

                    if (!filters.ecosystems.find(item => item.value === name)) {
                        filters.ecosystems = [
                            ...filters.ecosystems,
                            {
                                name: name,
                                value: name,
                            }
                        ];
                    }
                })
            }
        } else {
            loadListResolvers.tags();
            loadListResolvers.categories();
            loadListResolvers.clients();
        }

        if (searchType === constants.SEARCH_TYPE_STARTUPS) {
            getTopClientsList();
        }

        loadListResolvers.settings();
    }
}

async function getColumns() {
    const {data} = await userColumnsService.getColumns('search-' + searchType);
    gridColumns.value = [];

    if (searchType === constants.SEARCH_TYPE_STARTUPS) {
        gridColumns.value = data.columns;
        searchParams.includeHidden = !!data.params.include_hidden_startups;
    } else {
        gridColumns.value = data;
    }

    gridColumns.value.forEach(column => {
        if (columnsComponentsNames[column.name] !== undefined) {
            column.component = columnsComponentsNames[column.name];
        }

        if (column.name.startsWith('custom_') || column.name.startsWith('diligence_')) {
            column.component = 'CustomField';
        }
    });

    loadListResolvers.columns();
}

function checkCategories() {
    if (searchCategory && filtersOptions.categories.length && !filters.categories.length) {
        filters.categories = filtersOptions.categories.filter(category => category.value === searchCategory);

        nextTick(() => {
            loadListResolvers.categories();
        });
    }
}

function checkTags() {
    if (searchTag && store.getters.tagsLoaded && filtersOptions.tags.length && !filters.tags.length) {
        filters.tags = filtersOptions.tags.filter(tag => tag.value === searchTag);

        nextTick(() => {
            loadListResolvers.tags();
        });
    }
}

function checkClients() {
    if (searchClient && filtersOptions.clients.length) {
        filters.clients = [
            {
                name: searchClient,
                value: searchClient,
            }
        ];

        if (!filtersOptions.clients.find(item => item.value === searchClient)) {
            filtersOptions.clients.push({
                name: searchClient,
                value: searchClient,
            })
        }

        nextTick(() => {
            loadListResolvers.clients();
        });
    }
}

async function precessUserSearch() {
    const userQuery = await getUserSearch(userSearch);

    if (userQuery) {
        populateFilters(userQuery.rules);
        query.rules = userQuery.rules;

        if (userQuery.prompt_query) {
            promptQuery.rules = userQuery.prompt_query.rules;
            promptQuery.params = userQuery.prompt_query.params;
        }
    }

    loadListResolvers.userSearch();
}

async function getList(resetPage = true) {
    if (!canLoadList.value) {
        return;
    }

    if (store.state.user.show_similar) {
        let index = gridColumns.value.findIndex(item => item.name === 'relevancy');

        if (filters.similarTracker) {
            if (index < 0) {
                gridColumns.value.splice(1, 0, {
                    label: 'Relevancy',
                    name: 'relevancy',
                })
            }
        } else {
            if (index >= 0) {
                gridColumns.value.splice(index, 1)
            }
        }
    }

    if (resetPage) {
        startupsGrid.value.resetList();
    } else {
        startupsGrid.value.updateList();
    }
}

function getListMethod(pageParams) {
    let requestParams;

    if (customView === constants.CUSTOM_VIEW_MY_STARTUPS) {
        requestParams = {
            searchString: searchString.value
        }
    } else {
        requestParams = {
            searchType,
            query: {
                main: query,
                prompt: promptQuery,
            },

            params: searchParams,
        }
    }

    return customView === constants.CUSTOM_VIEW_MY_STARTUPS
        ? startupsService.getAddedByCompany({requestParams, pageParams})
        : searchService.getList({requestParams, pageParams});
}

function getAIListMethod(pageParams) {
    let requestParams = {
        searchType,
        query: {
            main: query,
            prompt: {
                ...promptQuery,
                semantic: queryText.value,
                hybrid: hybrid.value
            },
        },

        params: searchParams,
    }

    return searchService.getList({requestParams, pageParams});
}

function changeView(viewType) {
    if (viewType === constants.DISPLAY_TYPE_RADAR) {
        asideTabIndex.value = 1
        leftCollapsed.value = false;
    } else {
        asideTabIndex.value = 0;
    }

    displayType.value = viewType;
}

function gridAction(params) {
    if (params.action === 'tag-clicked') {
        let tag = filtersOptions.tags.find(item => item.name === params.tagName);

        if (tag && !filters.tags.some(item => item.name === tag.name)) {
            filters.tags = [...filters.tags, tag];
        }
    } else if (params.action === 'invite') {
        emit('invite', params.rowId);
    } else {
        gridActions.value.action(params);
    }
}

function openSaveSearch() {
    saveSearchModal.value.openSave();
}

function manageSavedSearches() {
    saveSearchModal.value.openManage();
}

function reloadSavedSearches() {
    getSavedSearches();
}

function updateSavedSearch() {
    saveSearchModal.value.updateQuery(savedSearch.value.id);
    canUpdateSavedSearch.value = false;
}

function searchStringChanged() {
    getList(true);
}

async function updateIsFavorite(id, isFavorited) {
    await ecosystemsService.setFavorite(id, isFavorited);
    startupsGrid.value.items.find(startup => startup.id === id).name.is_favorited = isFavorited;
}

function invertRuleLocal(rule, isPromptQuery) {
    if (rule.name === "ecosystems" && store.getters.isFreeEnterprise) {
        needUpgradeModal.value.show();
        return;
    }

    invertRule(rule, isPromptQuery);
}

function deleteRuleLocal(rule, isPromptQuery) {
    if (rule.name === "ecosystems" && store.getters.isFreeEnterprise) {
        needUpgradeModal.value.show();
        return;
    }

    deleteRule(rule, isPromptQuery);
}

function clearAll() {
    deleteAllRules();
    savedSearch.value = null;
}


// Providers
// -------------------------------------------------------------------------------------------------
provide('searchType', searchType);
provide('query', query);
provide('promptQuery', promptQuery);
provide('searchParams', searchParams);
provide('customView', customView);
provide('showRadarSwitcher', showRadarSwitcher);
provide('canUpdateSavedSearch', canUpdateSavedSearch);
provide('isRadarView', isRadarView);
provide('itemsIds', itemsIds);
provide('queryText', queryText);
provide('negativeQueryText', negativeQueryText);
provide('gridParams', computed(() => startupsGrid.value?.gridParams));
provide('isRadarView', isRadarView);
provide('elasticQuery', elasticQuery);
provide('gridType', gridType);
provide('hybrid', hybrid);
provide('filters', filters);
provide('savedSearch', savedSearch);
provide('savedSearches', savedSearches);
provide('saveSearchModal', saveSearchModal);

provide('toggleCondition', toggleCondition);
provide('getColumns', getColumns);
provide('updateIsFavorite', updateIsFavorite);
provide('deleteRule', deleteRuleLocal);
provide('invertRule', invertRuleLocal);
provide('changeView', changeView);
provide('openSaveSearch', openSaveSearch);
provide('updateSavedSearch', updateSavedSearch);
provide('manageSavedSearches', manageSavedSearches);
provide('reloadSavedSearches', reloadSavedSearches);
</script>

<template>
    <div>
        <TopBar
            :search-type="searchType"
            :search-string.sync="searchString"
            :ai-loading="aiLoading"
            :ai-failed="aiFailed"
            :search-params="searchParams"
            :suggested-ai-params="suggestedAiParams"
            :aside-tab-index="asideTabIndex"
            @keywordAdded="addKeyword"
            @changeView="changeView"
            @deleteAllRules="clearAll"
            @getAiQueryParams="getAIQueryParams"
            @applyAIQueryParams="applyAiQueryParams"
            @getList="getList"
            @searchStringChanged="searchStringChanged"
        />

        <div>
            <div>
                <main class="main main--search main--scrolled" :class="{'main--collapsed': leftCollapsed}">
                    <SideBar
                        v-if="!customView"
                        ref="topBar"
                        :is-radar-view="isRadarView"
                        :search-type="searchType"
                        :aside-tab-index.sync="asideTabIndex"
                        :saved-search.sync="savedSearch"
                        :saved-searches="savedSearches"
                        :filters="filters"
                        :filters-options="filtersOptions"
                        :custom-data-filter="customDataFilter"
                        :search-params="searchParams"
                        :query="query"
                        @getTopInvestorsList="getTopInvestorsList"
                        @getInvestorsList="getInvestorsList"
                        @updateStartupLocationsList="getLocationsList"
                        @updateClientsList="getClientsList"
                        @updateEcosystemsList="getEcosystemsList"
                        @updateCustomDataFilter="updateCustomDataFilter"
                    />

                    <div class="content">
                        <div class="content__inner" style="position: relative">
                            <StartupsGrid
                                v-if="customView || store.getters.isEcosystem"
                                ref="startupsGridMain"
                                :show="!isRadarView && gridType === 'main'"
                                :columns="gridColumns"
                                :sort="gridSort"
                                :get-list-method="getListMethod"
                                :grid-action="gridAction"
                            />

                            <StartupsAdvancedGrid
                                v-else
                                ref="startupsGridMain"
                                :show="!isRadarView && gridType === 'main'"
                                :columns="gridColumns"
                                :sort="gridSort"
                                :grid-action="gridAction"
                            />

                            <StartupsGrid
                                v-if="searchType === constants.SEARCH_TYPE_STARTUPS"
                                ref="startupsGridAI"
                                :show="!isRadarView && gridType === 'ai'"
                                :columns="gridColumns"
                                :sort="gridSort"
                                :get-list-method="getAIListMethod"
                                :grid-action="gridAction"
                            />

                            <template v-if="startupsGrid?.items.length">
                                <Radar v-if="isRadarView"
                                       :query="query"
                                       :prompt-query="promptQuery"
                                       :search-params="searchParams"
                                />
                            </template>
                        </div>
                    </div>
                </main>
            </div>
        </div>

        <SaveSearch
            ref="saveSearchModal"
            :search-type="searchType"
            :search-data="searchData"
            :saved-searches-list="savedSearches"
            @saved-search-changed="reloadSavedSearches"
        />

        <SearchGridActions
            ref="gridActions"
            :items="startupsGrid?.items"
        />

        <Helper
            v-if="searchType === constants.SEARCH_TYPE_STARTUPS && store.getters.isEnterprise"
            page="search"
        />

        <helper
            v-if="searchType === constants.SEARCH_TYPE_ECOSYSTEMS && store.getters.isEnterprise"
            page="ecosystems"
        />

        <b-modal ref="needUpgradeModal" modal-class="modal-show-upgrade modal-w-md" :hide-footer="true" title="Discover over 450,000 more startups">
            <p>Upgrade your SwitchPitch membership to find, access and manage startup relationships.</p>

            <div class="modal-footer">
                <a class="btn btn-primary btn-lg" href="mailto:info@switchpitch.com">Contact Sales</a>
            </div>
        </b-modal>
    </div>
</template>

<style scoped>

</style>
